import { toEntityPayload } from "@/components/alerts/add-v2/model/adapters";
import { BulletinFormValues } from "@/components/alerts/add-v2/model/types";
import {
  ALERT_CLASSIFICATION,
  BULLETIN_TYPE,
  defaultAlertProperties,
  LOCATION_CLASSIFICATION,
} from "@/components/alerts/constants";
import { IAlertProperties, PlaceType } from "@/types";

/**
 * `BulletinFormValues` → the legacy `alertProperties` bag the preview consumes (README decision 1).
 *
 * Phase 1 maps the scalar fields and the narrative — enough to prove `DisplayAlert` renders inside
 * the form page without being modified, which is the risk this phase was asked to retire early.
 * Phase 5 adds the media, which costs nothing extra: the files are already on the bulletin row, so
 * their URLs are the same ones the finished bulletin will serve. Entities and addresses arrive the same
 * way, in the wire shape the detail view reads them in; resolutions stay empty.
 */
export const toAlertProperties = (
  values: BulletinFormValues,
): IAlertProperties => ({
  ...defaultAlertProperties,

  policeService: values.policeService,
  type: values.bulletinType as BULLETIN_TYPE | null,
  occurrenceNumbers: values.occurrenceNumbers,
  operationalArea: values.operationalArea,

  associatedCrimes: values.associatedCrimes,
  // A row with no start date would be formatted as the epoch by the preview, so an untouched
  // incident time is left out entirely rather than shown as 1969.
  incidentTimes: values.incidentTimes
    .filter(
      (time) =>
        time.startDate !== null ||
        time.timeType === "unknown" ||
        time.timeType === "not_applicable",
    )
    .map((time) => ({
      time_type: time.timeType,
      start_date: time.startDate,
      end_date: time.endDate,
    })),

  // Keyed by filename, which is how the bulletin row stores them and how `DisplayAlert` reads them.
  evidence_files: Object.fromEntries(
    values.media.map((item) => [
      item.filename,
      {
        filename: item.filename,
        content_type: item.contentType,
        download_url: item.url,
        // The preview thumbnails everything that is not a video, so a file without a thumbnail
        // falls back to itself rather than to a broken image.
        thumbnail_url: item.thumbnailUrl ?? item.url,
        sort_ordinal: item.sortOrdinal,
        // What captions the hero with the person in it. `entity_ids` is optional on
        // `IEvidenceFile` -- files predate the relation -- so leaving it out cost the preview its
        // captions without costing a type error.
        entity_ids: item.entityIds,
        openai_description: {},
      },
    ]),
  ),
  pdf: values.pdf
    ? {
        filename: values.pdf.filename,
        content_type: values.pdf.contentType,
        download_url: values.pdf.url,
      }
    : {},

  description: values.description || null,
  descriptionFr: values.descriptionFr || null,
  originalLanguage: values.originalLanguage,

  contacts: values.contacts.map((contact) => ({
    name: contact.name,
    email: contact.email,
    phone_number: contact.phoneNumber,
    extension: contact.extension,
    badge_number: contact.badgeNumber,
    error: false,
  })),
  approvedBy: values.approvedBy || null,
  approvedByBadge: values.approvedByBadge || null,
  classification: values.classification ?? ALERT_CLASSIFICATION.PROTECTED_A,
  issuedTime: values.issuedTime,

  // Through `toEntityPayload`, so the preview shows what the save sends rather than a second
  // projection of the same rows. The entity list itself is shared with the published bulletin —
  // `components/alerts/entities` — and belongs to neither creation flow.
  entities: values.entities.map(toEntityPayload),

  /*
   * Filtered and read exactly as `toAddressPayload` does it, because the preview has to show what
   * gets saved: the same "a row nobody typed into is scaffolding" predicate, and the same preference
   * for the **prediction's** string over the geocoder's — the two disagree often enough to matter and
   * the officer picked the prediction.
   *
   * `defaultAlertProperties` seeds this with `[null]`, which the detail view used to render as a
   * blank labelled row, so an empty list is still the right answer for a form with no address yet.
   */
  rawAddresses: values.locations
    .filter((location) => location.input !== null || location.resolved !== null)
    .map(
      (location): PlaceType => ({
        description:
          location.input?.description ??
          location.resolved?.formattedAddress ??
          "",
        place_id: location.resolved?.placeId ?? location.input?.place_id ?? "",
        structured_formatting: location.input?.structured_formatting ?? {
          main_text: "",
          secondary_text: "",
        },
        // The detail view labels an address by its type as soon as there is more than one, so an
        // untyped row previews as what it will be saved as rather than as nothing.
        location_classification:
          location.classification ?? LOCATION_CLASSIFICATION.INCIDENT_ADDRESS,
        other_location_classification: location.otherClassification,
        unit: location.unit,
      }),
    ),
  isDraft: true,
});
